import type {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult
} from '@tanstack/react-query';

import type { Account, Profile, Entry } from './types/cit-public-api';
import { CitOrigin } from './types/cit-public-api';
import { BASE_URL } from './constants';

// local hubpro has no real host or subdomain to build an origin from
const IS_DEV = process.env.NODE_ENV === 'development';

export interface PaginatedResponse<T> {
  entries: T[];
  total: number;
  cursor: string | undefined;
}

/**
 * Whether entry is the one a url names. Slugs are only unique within an account, and the all-search
 * and classification lists span accounts, so the account has to match as well as the entry
 *
 * @param entry
 * @param entryId - the url's entry segment, a slug or an id
 * @param accountId - the url's account segment, either the account's slug or its id, since a url can
 * use the id even when the account has a slug; undefined on HUB Pro, whose lists hold one account
 * @returns true when both the entry and its account match
 */
function isEntry(entry: Entry, entryId: string, accountId: string | undefined) {
  if (entry.slug !== entryId && entry.id !== entryId) {
    return false;
  }

  if (accountId === undefined) {
    return true;
  }

  // the main app accepts all-digit account slugs, but the public API reads an all-digit segment as
  // an id, so match it the same way the API resolves it
  return /^\d+$/.test(accountId)
    ? entry.account_id === accountId
    : entry.account_slug === accountId;
}

export async function getPrevOrNextEntry(
  pages: PaginatedResponse<Entry>[] | undefined,
  entryId: string,
  accountId: string | undefined,
  direction: 'prev' | 'next',
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<InfiniteData<PaginatedResponse<Entry>, unknown>, Error>>
): Promise<Entry | undefined> {
  if (pages) {
    for (const [pageIdx, page] of pages.entries()) {
      const idx = page.entries.findIndex((entry) => isEntry(entry, entryId, accountId));
      if (idx > -1) {
        if (direction === 'prev') {
          const prevEntryId =
            idx === 0
              ? pageIdx === 0
                ? undefined
                : pages[pageIdx - 1].entries[pages[pageIdx - 1].entries.length - 1]
              : page.entries[idx - 1];

          return prevEntryId;
        } else {
          let nextEntryId =
            idx === page.entries.length - 1
              ? pageIdx === pages.length - 1
                ? undefined
                : pages[pageIdx + 1].entries[0]
              : page.entries[idx + 1];

          if (!nextEntryId && fetchNextPage) {
            const result = await fetchNextPage();
            const updatedPages = result.data?.pages;
            nextEntryId = updatedPages?.[pageIdx + 1]?.entries[0];
          }

          return nextEntryId;
        }
      }
    }
  }

  return undefined;
}

export async function getPrevOrNextProfile(
  pages: PaginatedResponse<Profile>[] | undefined,
  profileId: string,
  direction: 'prev' | 'next',
  fetchNextPage?: (
    options?: FetchNextPageOptions
  ) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<{ entries: Profile[]; total: number; cursor: string | undefined }, unknown>,
      Error
    >
  >
): Promise<Profile | undefined> {
  if (pages) {
    for (const [pageIdx, page] of pages.entries()) {
      const idx = page.entries.findIndex(
        (profile) => profile.slug === profileId || profile.id === profileId
      );
      if (idx > -1) {
        if (direction === 'prev') {
          const prevProfileId =
            idx === 0
              ? pageIdx === 0
                ? undefined
                : pages[pageIdx - 1].entries[pages[pageIdx - 1].entries.length - 1]
              : page.entries[idx - 1];

          return prevProfileId;
        } else {
          let nextProfileId =
            idx === page.entries.length - 1
              ? pageIdx === pages.length - 1
                ? undefined
                : pages[pageIdx + 1].entries[0]
              : page.entries[idx + 1];

          if (!nextProfileId && fetchNextPage) {
            const result = await fetchNextPage();
            const updatedPages = result.data?.pages;
            nextProfileId = updatedPages?.[pageIdx + 1]?.entries[0];
          }

          return nextProfileId;
        }
      }
    }
  }

  return undefined;
}

/**
 * Gets an array window/slice of entries from the provided pages centered around a specific entry as well as the previous and next entry ids without flat mapping all of the pages
 * @param pages - The pages of entries from the react-query data
 * @param entryId - The id of the entry to center the window around
 * @param accountId - The url's account segment for that entry, undefined on HUB Pro
 * @param windowSize - The number of entries on each side of the target entry to include in the window
 * @param fetchNextPage - The function to fetch the next page, is undefined if next page is not available
 */
export function getEntryWindow(
  pages: PaginatedResponse<Entry>[] | undefined,
  entryId: string,
  accountId: string | undefined,
  windowSize: number,
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<InfiniteData<PaginatedResponse<Entry>, unknown>, Error>>
): (Entry | undefined)[] {
  if (!pages) {
    return [];
  }

  const entryWindow: (Entry | undefined)[] = [];

  // First find the target entry and its position
  let targetPageIdx = -1;
  let targetIdx = -1;

  for (const [pageIdx, page] of pages.entries()) {
    const idx = page.entries.findIndex((entry) => isEntry(entry, entryId, accountId));
    if (idx > -1) {
      targetPageIdx = pageIdx;
      targetIdx = idx;
      // Add the target entry to the result
      entryWindow.push(page.entries[idx]);
      break;
    }
  }

  // If the target entry is not found, return an empty array
  if (targetPageIdx === -1 || targetIdx === -1) {
    return [];
  }

  // Add entries before the target
  let entriesBefore = 0;
  let currentPageIdx = targetPageIdx;
  let currentIdx = targetIdx - 1;

  while (entriesBefore < windowSize) {
    if (currentIdx >= 0) {
      const entry = pages[currentPageIdx].entries[currentIdx];
      if (entry) {
        entryWindow.unshift(entry);
        entriesBefore++;
      }
      currentIdx--;
    } else if (currentPageIdx > 0) {
      currentPageIdx--;
      currentIdx = pages[currentPageIdx].entries.length - 1;
    } else {
      entryWindow.unshift(undefined);
      entriesBefore++;
    }
  }

  // Add entries after the target
  let entriesAfter = 0;
  currentPageIdx = targetPageIdx;
  currentIdx = targetIdx + 1;

  while (entriesAfter < windowSize) {
    if (currentIdx < pages[currentPageIdx].entries.length) {
      const entry = pages[currentPageIdx].entries[currentIdx];
      if (entry) {
        entryWindow.push(entry);
        entriesAfter++;
      }
      currentIdx++;
    } else if (currentPageIdx < pages.length - 1) {
      currentPageIdx++;
      currentIdx = 0;
    } else {
      // If we're at the end of the last page and we need more entries, fetch the next page
      if (fetchNextPage && entriesAfter < windowSize) {
        try {
          fetchNextPage();
        } catch (error) {
          console.error('Error fetching next page', error);
        }
      } else {
        entryWindow.push(undefined);
        entriesAfter++;
      }
    }
  }

  return entryWindow;
}

/**
 * Gets an array window/slice of entries from the provided pages centered around a specific profile as well as the previous and next profile ids without flat mapping all of the pages
 * @param pages - The pages of profiles from the react-query data
 * @param profileId - The id of the profile to center the window around
 * @param windowSize - The number of profiles on each side of the target profile to include in the window
 * @param fetchNextPage - The function to fetch the next page, is undefined if next page is not available
 */
export function getProfileWindow(
  pages: PaginatedResponse<Profile>[] | undefined,
  profileId: string,
  windowSize: number,
  fetchNextPage?: (options?: FetchNextPageOptions) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          entries: Profile[];
          total: number;
          cursor: string | undefined;
        },
        unknown
      >,
      Error
    >
  >
): (Profile | undefined)[] {
  if (!pages) {
    return [];
  }

  const profileWindow: (Profile | undefined)[] = [];

  // First find the target profile and its position
  let targetPageIdx = -1;
  let targetIdx = -1;

  for (const [pageIdx, page] of pages.entries()) {
    const idx = page.entries.findIndex(
      (profile) => profile.slug === profileId || profile.id === profileId
    );
    if (idx > -1) {
      targetPageIdx = pageIdx;
      targetIdx = idx;
      // Add the target profile to the result
      profileWindow.push(page.entries[idx]);
      break;
    }
  }

  // If the target profile is not found, return an empty array
  if (targetPageIdx === -1 || targetIdx === -1) {
    return [];
  }

  // Add profiles before the target
  let profilesBefore = 0;
  let currentPageIdx = targetPageIdx;
  let currentIdx = targetIdx - 1;

  while (profilesBefore < windowSize) {
    if (currentIdx >= 0) {
      const entry = pages[currentPageIdx].entries[currentIdx];
      if (entry) {
        profileWindow.unshift(entry);
        profilesBefore++;
      }
      currentIdx--;
    } else if (currentPageIdx > 0) {
      currentPageIdx--;
      currentIdx = pages[currentPageIdx].entries.length - 1;
    } else {
      profileWindow.unshift(undefined);
      profilesBefore++;
    }
  }

  // Add entries after the target
  let profilesAfter = 0;
  currentPageIdx = targetPageIdx;
  currentIdx = targetIdx + 1;

  while (profilesAfter < windowSize) {
    if (currentIdx < pages[currentPageIdx].entries.length) {
      const profile = pages[currentPageIdx].entries[currentIdx];
      if (profile) {
        profileWindow.push(profile);
        profilesAfter++;
      }
      currentIdx++;
    } else if (currentPageIdx < pages.length - 1) {
      currentPageIdx++;
      currentIdx = 0;
    } else {
      // If we're at the end of the last page and we need more profiles, fetch the next page
      if (fetchNextPage && profilesAfter < windowSize) {
        try {
          fetchNextPage();
        } catch (error) {
          console.error('Error fetching next page', error);
        }
      } else {
        profileWindow.push(undefined);
        profilesAfter++;
      }
    }
  }

  return profileWindow;
}

/**
 * Slugs reach us in either form - already percent-encoded ('Fine%20Art') or raw ('Fine Art') -
 * so decode first and always re-encode. That makes the result identical either way, and safe to
 * call more than once on the same value.
 *
 * @param slug
 * @returns the slug in its percent-encoded, url-safe form
 */
export function toUrlSafeSlug(slug: string) {
  let decoded = slug;

  try {
    decoded = decodeURIComponent(slug);
  } catch {
    // a stray '%' makes decoding throw, which means the slug was never encoded to begin with
  }

  return encodeURIComponent(decoded);
}

/**
 * TODO: remove if we decide to store full origin instead
 * account.host is stored as a bare domain, so it needs a scheme before it can be used as a url
 *
 * @param host
 * @returns the host with an https:// scheme, left alone if it already carries one
 */
export function withScheme(host: string) {
  return /^https?:\/\//.test(host) ? host : `https://${host}`;
}

/**
 * on hubpro BASE_URL is a wildcard template, so it is only a real origin once the
 * account's subdomain replaces the '*'
 *
 * @param account
 * @returns the account's hubpro.io origin, with no trailing slash
 */
export function getSubdomainOrigin(account: Account) {
  return BASE_URL.replace('*', account.subdomain ?? account.slug);
}

/**
 * scheme + host only, which on hubpro is the account's own domain rather than the hub's
 *
 * @param account
 * @param citOrigin
 * @returns the origin the account's pages hang off of, with no trailing slash
 */
export function getOrigin(account: Account, citOrigin: CitOrigin) {
  return citOrigin === CitOrigin.HUBPRO && !IS_DEV
    ? account.host
      ? withScheme(account.host)
      : getSubdomainOrigin(account)
    : BASE_URL;
}

// Helper function with proper typing
// the id after /entry/ or /instance/ in a pathname, percent-encoded like the matching route param
export function getIdFromPathname(pathname: string, segment: 'entry' | 'instance') {
  return pathname.match(new RegExp(`/${segment}/([^/?#]+)`))?.[1];
}

// the account segment of an all-search or classification entry url, percent-encoded like the route param
export function getAccountIdFromPathname(pathname: string) {
  return pathname.match(/\/(?:search|classifications\/[^/]+)\/([^/]+)\/entry\//)?.[1];
}

export function getEntryLink(
  entryId: string,
  citOrigin: CitOrigin,
  accountId?: string,
  classificationId?: string,
  folderId?: string,
  fromAccountSearch?: boolean,
  fromAllSearch?: boolean
) {
  if (classificationId) {
    return `${
      citOrigin === CitOrigin.IFRAME ? `/iframe` : ''
    }/classifications/${classificationId}/${accountId}/entry/${entryId}`;
  } else if (fromAllSearch) {
    return `${citOrigin === CitOrigin.IFRAME ? `/iframe` : ''}/search${citOrigin !== CitOrigin.HUBPRO ? `/${accountId}` : ''}/entry/${entryId}`;
  } else if (fromAccountSearch) {
    return `${citOrigin === CitOrigin.IFRAME ? `/iframe` : ''}${citOrigin !== CitOrigin.HUBPRO ? `/${accountId}` : ''}/entry/${entryId}`;
  } else if (folderId === 'all' || !folderId) {
    return `${citOrigin === CitOrigin.IFRAME ? `/iframe` : ''}${citOrigin !== CitOrigin.HUBPRO ? `/${accountId}` : ''}/folder/entry/${entryId}`;
  } else {
    return `${citOrigin === CitOrigin.IFRAME ? `/iframe` : ''}${citOrigin !== CitOrigin.HUBPRO ? `/${accountId}` : ''}/folder/${folderId}/entry/${entryId}`;
  }
}

export function getFolderLink(citOrigin: CitOrigin, accountId?: string, folderId?: string) {
  // passing no folderId will get all entries for the account
  return `${citOrigin === CitOrigin.IFRAME ? `/iframe` : ''}${citOrigin !== CitOrigin.HUBPRO ? `/${accountId}` : ''}/folder${folderId ? `/${folderId}` : ''}`;
}

export function getClassificationLink(classificationId: string, encode = false) {
  return `/classifications/${encode ? encodeURIComponent(classificationId) : classificationId}`;
}

export function getProfileTypeLink(
  citOrigin: CitOrigin,
  accountId?: string,
  profileType?: string,
  encode = false
) {
  return `${citOrigin === CitOrigin.IFRAME ? `/iframe` : ''}${citOrigin !== CitOrigin.HUBPRO ? `/${accountId}` : ''}/profiles${
    profileType ? `/${encode ? encodeURIComponent(profileType) : profileType}` : ''
  }`;
}

export function getProfileInstanceLink(
  profileType: string,
  profileId: string,
  citOrigin: CitOrigin,
  accountId?: string,
  encodeProfileType = false,
  encodeProfileId = false
) {
  return `${citOrigin === CitOrigin.IFRAME ? `/iframe` : ''}${
    citOrigin !== CitOrigin.HUBPRO ? `/${accountId}` : ''
  }/profiles/${
    encodeProfileType ? encodeURIComponent(profileType) : profileType
  }/instance/${encodeProfileId ? encodeURIComponent(profileId) : profileId}`;
}

// utility function that adds the testTheme query param to an href when present
// this pairs with the proxy/middleware and getCitThemeServer to allow for preserving the test theme when clicking links in the theme-tester iframe
export function addTestThemeToHref(href: string, testTheme: string | undefined | null): string {
  if (!testTheme) {
    return href;
  }
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}testTheme=${encodeURIComponent(testTheme)}`;
}
